import * as React from 'react';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { columns } from './internals/data/gridData';
import { alpha, styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import ChatModal from './modals/ChatModal';

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
      cursor: 'pointer',
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

export default function CustomizedDataGrid(props) {
  const [rows, setRows] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [selectedUsersId, setSelectedUsersId] = React.useState([]);
  const { data, selectedRow, setSelectedRow } = props;

  React.useEffect(() => {
    if (selectedRow) {
      //listado de user_id sin repetir obtenido desde selectedRow.mensajes
      const usersId = selectedRow.mensajes.map(item => item.user_id);
      setSelectedUsersId([...new Set(usersId)]);
    }
  }, [selectedRow]);

  const addExtraData = data => {
    return data.map(item => {
      return {
        ...item,
        id: item.id_campana,
        nombre: item.nombre,
        template: item.template,
        fecha: item.fecha,
        sentMessages: item.total,
        readMessages: item.leido,
        recievedResponses: item.respuestas,
        errors: item.error,
        dailyResponses: [
          166896, 190041, 248686, 226746, 261744, 271890, 332176, 381123,
          396435, 495620, 520278, 460839, 704158, 559134, 681089, 712384,
          765381, 771374, 851314, 907947, 903675, 1049642, 1003160, 881573,
          1072283, 1139115, 1382701, 1395655, 1355040, 1381571,
        ],
      };
    });
  };

  const altColumns = [
    { field: 'id', headerName: 'ID', flex: 0.2, minWidth: 40 },
    { field: 'user_id', headerName: 'User ID', flex: 0.4, minWidth: 40 },
    { field: 'fecha', headerName: 'Fecha', flex: 0.5, minWidth: 40 },
    {
      field: 'actualizacion',
      headerName: 'Actualización',
      flex: 0.5,
      minWidth: 40,
    },
    { field: 'estado', headerName: 'Estado', flex: 0.3, minWidth: 20 },
    {
      field: 'clasification',
      headerName: 'Clasificación',
      flex: 0.3,
      minWidth: 40,
      renderCell: params => (
        <Tooltip
          title={
            params.value === 'Grupo 0'
              ? 'Grupo 0: Cualquier otra situación no contemplada en los grupos anteriores'
              : params.value === 'Grupo 1'
              ? 'Grupo 1: Indica que el usuario ya pagó o se puso al día con su deuda.'
              : params.value === 'Grupo 2'
              ? 'Grupo 2: Indica que se acercara a cancelar o pagara durante la semana'
              : params.value === 'Grupo 3'
              ? 'Grupo 3: Expresa que la cobranza es a otra persona, él no es el deudor o el número está equivocado'
              : params.value === 'Grupo 4'
              ? 'Grupo 4: Muestra desinterés, rechazo o contiene algún insulto'
              : 'Respuesta sin clasificar'
          }>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    { field: 'body', headerName: 'Body', flex: 1, minWidth: 40 },
  ];

  const columnsWithErrors = [
    ...altColumns.filter(
      column => column.field !== 'body' && column.field !== 'clasification',
    ),
    { field: 'error', headerName: 'Error', flex: 1.2, minWidth: 40 },
  ];

  React.useEffect(() => {
    if (selectedRow && selectedRow.mensajes) {
      setRows(selectedRow?.mensajes);
    } else {
      if (data) {
        setRows(addExtraData(data));
      }
    }
  }, [selectedRow, data]);

  const hasErrors = rows.some(row => row.error);

  return (
    <>
      <StripedDataGrid
        autoHeight
        onCellClick={params => {
          if (params?.row?.mensajes) {
            setSelectedRow(params.row);
          } else {
            if (params.row.estado === 'answered') {
              setOpen(true);
            }
          }
        }}
        rows={rows}
        columns={
          selectedRow ? (hasErrors ? columnsWithErrors : altColumns) : columns
        }
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableColumnResize
        density="compact"
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />
      <ChatModal
        open={open}
        conversation_id={selectedRow?.id_masivo}
        selectedUsersId={selectedUsersId}
        handleClose={() => {
          setOpen(false);
        }}
        title="Test Modal"
      />
    </>
  );
}
